import { useLocation } from '@gatsbyjs/reach-router';
import { Container } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { type FallbackProps, withErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import BigCircle from '@components/BigCircle';
import { DrilldownHeader } from '@components/DrilldownHeader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';

import { STRESS_INDEX, VITAL_SIGN_TYPES } from '@constants/fhir';

import { HealthChartProvider } from '@contexts/index';

import { useIsMobile } from '@hooks/useIsMobile';

import AppLayout from '@layouts/AppLayout';

import type { TrackerType } from '@models/vital-sign';
import { HEALTH_TRACKER_MAP } from '@models/vital-sign';

import Actions from './Actions';
import Content from './Content';
import UnitRange from './Content/Header/UnitRange';

interface HealthTrackerDrilldownProps {
  category: TrackerType;
  type?: string;
  /** Should alerts be fetched and displayed? */
  showAlerts?: boolean;
}

const HealthTrackerDrilldown: React.FC<HealthTrackerDrilldownProps> = ({
  category,
  type = '',
  showAlerts,
}) => {
  const isMobile = useIsMobile();
  const { formatMessage } = useIntl();
  const { backRoute } = HEALTH_TRACKER_MAP[category];

  const VITAL_SIGN_TYPE_DATA =
    type in VITAL_SIGN_TYPES ? VITAL_SIGN_TYPES[type] : undefined;

  const vitalSignLabel = VITAL_SIGN_TYPE_DATA?.messageId
    ? formatMessage({ id: VITAL_SIGN_TYPE_DATA.messageId })
    : '-';

  React.useEffect(() => {
    if (!VITAL_SIGN_TYPE_DATA || type === STRESS_INDEX) {
      navigate(backRoute);
    }
  }, [backRoute, type, VITAL_SIGN_TYPE_DATA]);

  return (
    <AppLayout friendlyName={vitalSignLabel}>
      <HealthChartProvider
        category={category}
        code={type}
        showAlerts={showAlerts}
      >
        <BigCircle />
        <DrilldownHeader
          backRoute={backRoute}
          buttonsElement={<Actions />}
          title={vitalSignLabel}
          showBackground={isMobile}
        >
          <UnitRange />
        </DrilldownHeader>

        <Container>
          <Content />
        </Container>
      </HealthChartProvider>
    </AppLayout>
  );
};

const Fallback = (props: FallbackProps) => {
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();

  const getTitle = () => {
    if (pathname.includes('body')) {
      return 'body';
    } else if (pathname.includes('vital-signs')) {
      return 'vitalSign';
    } else if (pathname.includes('fitness')) {
      return 'fitness';
    } else if (pathname.includes('sleep')) {
      return 'sleep';
    } else if (pathname.includes('health-scores')) {
      return 'healthScore';
    }
  };

  const title = formatMessage({
    id: `fallbackDrilldown.${getTitle()}` || 'fallbackDrilldown.record',
  });

  return (
    <AppLayout friendlyName={title}>
      <DrilldownHeader backRoute="/" showBackground title={title} />
      <Container maxWidth="md">
        <ErrorBoundaryFallback {...props} />
      </Container>
    </AppLayout>
  );
};

export default withErrorBoundary(HealthTrackerDrilldown, {
  FallbackComponent: (props) => <Fallback {...props} />,
});
